import React, {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {Button, EMPHASIS, FlexRow, Icon, Text, TextField, tokensForThemeProvider} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {MAX_LABEL_LENGTH} from './opportunityStageBands';

// Composed from design-system primitives rather than the DS table, which cannot express per-row
// validation state, locked rows, or an icon-only delete.
const Row = Styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 120px minmax(0, 1fr) 32px;
	align-items: center;
	gap: 16px 32px;
	/* One shorthand, because a later padding shorthand silently discards any longhand set above it. The
	   left offset gives the lock icon (and every row's first column) room from the row's edge; the right
	   one keeps the delete control off that edge, so the row reads as sitting inside the page rather than
	   running out of it. Both are matched on HeaderRow. */
	padding: 12px 16px 12px 8px;
`;
// The row and its error share one wrapper so the message reads as belonging to the whole row rather
// than to one field, and so it spans the full width instead of being clipped by the narrow end field.
const RowGroup = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;
// Lost and Won are not editable, so they sit back visually: muted fill and muted text, which also
// stops them competing with the editable rows between them.
const LockedRowGroup = Styled(RowGroup)`
	background: ${tokensForThemeProvider.sky.sys.color.background.disabled['1']};
`;
// The slot is always here, whether or not the row has anything to report. A message that takes up space
// only once it applies changes the page's height at the moment it appears — and it appears on blur, which
// is exactly what pressing a button does first. Everything below shifts down, including the Add and Reset
// controls under the table, so a press that began on one of them ends where that control no longer is and
// no click is delivered: the first press is spent revealing the message and only a second one acts.
// Holding the space costs a line per row and makes those controls answer the first time.
const RowError = Styled.div`
	min-height: 20px;
	padding-bottom: 4px;
`;
// Narrower than its column on purpose: the range column stays where it is, and the slack sits between
// the two, rather than the whole range column sliding left.
const NameField = Styled.div`
	max-width: 460px;
`;
// Padded on its leading edge rather than widening the grid's gap, which would space every column apart.
// The win probability and the range are both percentages, so without extra air between them the two read
// as one control. HeaderRow applies the same offset to the heading above it.
const RangeCell = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding-left: 24px;
`;
// A bound that cannot be typed into — the derived start, the pinned last end, and the fixed Lost/Won
// boundaries — carries its percent sign in the value itself, so digits and sign move and centre as one
// string. 80px is wide enough that '100%' never crowds its border, and every bound field on the page
// (editable or not) shares that width so the column reads as one set of equally sized boxes.
const BoundField = Styled.div`
	width: 80px;
	& input {
		text-align: center;
	}
`;
// The editable end's value can never include the sign — backspacing from the end would delete the sign
// instead of a digit, and the digit would then be stuck. Instead '%' is overlaid inside the field's own
// border rather than sitting outside it, so the field's bordered box is the full 80px width and reads
// as the same size as its disabled neighbours; only the value inside it stays plain digits.
const EditableBoundField = Styled.div`
	position: relative;
	width: 80px;
	& input {
		text-align: right;
		/* Clears the overlaid '%' — its glyph reaches about 22px in from the right edge, so the digits
		   have to stop short of that or the two visibly touch. */
		padding-right: 28px !important;
	}
`;
const PercentSuffix = Styled.span`
	position: absolute;
	top: 50%;
	right: 12px;
	transform: translateY(-50%);
	color: ${tokensForThemeProvider.sky.sys.color.text.content.auxiliary};
	pointer-events: none;
`;

// Holds the delete button and the text naming it. Positioned so the name can be taken out of the
// visual flow without leaving the cell.
const DeleteCell = Styled.div`
	position: relative;
`;
// Text for assistive technology only. The button cannot be given its name directly: it derives one
// from its own children or, having none, from its icon — announcing an icon-only button as "trash",
// the picture rather than the action — and it applies that after any name the caller passes, so a
// caller's aria-label is always discarded. A name referenced by id wins over one written inline, so
// this is how the button gets told what it does.
const AssistiveText = Styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	border: 0;
	overflow: hidden;
	white-space: nowrap;
	clip: rect(0 0 0 0);
	clip-path: inset(50%);
`;

/** A bound that cannot be edited, with its percent sign, so the whole reads as one centred value. */
const fixedBoundText = (value: number): string => (Number.isFinite(value) ? `${value}%` : '');

type LockedRowProps = {
	label: string;
	percentage: number;
	dataCy: string;
};

/** Lost and Won: fixed boundaries owned by the product, so no editable bound and no delete affordance. */
export const LockedOpportunityStageRow = ({label, percentage, dataCy}: LockedRowProps): JSX.Element => {
	const {formatMessage} = useIntl();

	return (
		<LockedRowGroup>
			<Row data-cy={dataCy}>
				<FlexRow alignItems={'center'} gap={'s'}>
					<Icon icon={'lockClosed'} size={'m'} color={'auxiliary'} />
					<Text color={'auxiliary'}>{label}</Text>
				</FlexRow>
				{/* Lost and Won are a single fixed value, so it sits in the win probability column and the
				    range column only says that it cannot be moved rather than repeating the number. */}
				<BoundField>
					<TextField
						value={fixedBoundText(percentage)}
						disabled
						aria-label={label}
						data-cy={`${dataCy}-percentage`}
					/>
				</BoundField>
				<RangeCell>
					<Text color={'auxiliary'}>
						{formatMessage({id: 'settings.opportunity_stages.fixed_always'}, {percentage: percentage})}
					</Text>
				</RangeCell>
				<div />
			</Row>
		</LockedRowGroup>
	);
};

type EditableRowProps = {
	index: number;
	name: string;
	start: number;
	/** The end bound exactly as typed, so a part-deleted value stays visible instead of becoming NaN. */
	endText: string;
	/**
	 * Whether this is the final band, whose end is pinned at 99 and therefore not editable. It has to be
	 * stated by the parent, which knows the set: inferring it from the value being 99 confuses "the last
	 * band" with "any band someone has just typed 99 into", and would disable a middle row mid-keystroke.
	 */
	isLast: boolean;
	/** The win probability exactly as typed, for the same reason the end bound is. */
	winProbabilityText: string;
	nameError?: string;
	endError?: string;
	winProbabilityError?: string;
	/**
	 * Whether this row should take the cursor. Set on a row that has just been appended, so the label
	 * field is where typing goes and, if the user leaves without typing, the blur that reports the empty
	 * label actually happens.
	 */
	autoFocus?: boolean;
	canDelete: boolean;
	onNameChange: (value: string) => void;
	onEndChange: (value: string) => void;
	onWinProbabilityChange: (value: string) => void;
	onCommit: () => void;
	onDelete: () => void;
};

/**
 * One customisable band. The start is derived and therefore never editable, and the last band's end is
 * pinned at 99. Values commit on blur, because a numeric range field passes through invalid
 * intermediate values on every keystroke.
 *
 * Validation reads as one message for the whole row: a bad end is a statement about the row's range,
 * not about the two characters inside the end field, and the field is far too narrow to show it.
 */
export const EditableOpportunityStageRow = ({
	index,
	name,
	start,
	endText,
	isLast,
	winProbabilityText,
	nameError,
	endError,
	winProbabilityError,
	autoFocus,
	canDelete,
	onNameChange,
	onEndChange,
	onWinProbabilityChange,
	onCommit,
	onDelete,
}: EditableRowProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const rowErrors = [nameError, winProbabilityError, endError].filter(Boolean) as string[];
	const deleteLabelId = `opportunity-stage-delete-label-${index}`;
	// The messages sit together under the row, so a field points at the whole block rather than at one
	// line of it. Announcing the row's other complaint alongside its own is the lesser problem: one
	// invalid row blocks the entire save, so a screen reader user needs to hear everything standing in
	// the way, not just the field they happen to be in.
	const rowErrorId = `opportunity-stage-row-error-${index}`;
	// The attributes that tell assistive technology a field was rejected and where the reason is, applied
	// to the inputs directly.
	//
	// The design system's text field takes a fixed list of props and forwards nothing else, so they
	// cannot be passed to it; and its own errorText renders the message as a detached element without
	// linking it either — neither aria-invalid nor aria-describedby reaches the input. Until the field
	// accepts them, setting them here is the only way the message is reachable by anything other than
	// eyesight. Worth a design system change so this can go.
	const rowRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const mark = (dataCy: string, message?: string) => {
			const input = rowRef.current?.querySelector<HTMLInputElement>(`input[data-cy="${dataCy}"]`);
			if (!input) {
				return;
			}
			if (message) {
				input.setAttribute('aria-invalid', 'true');
				input.setAttribute('aria-describedby', rowErrorId);
			} else {
				input.removeAttribute('aria-invalid');
				input.removeAttribute('aria-describedby');
			}
		};

		mark(`opportunity-stage-name-${index}`, nameError);
		mark(`opportunity-stage-win-probability-${index}`, winProbabilityError);
		mark(`opportunity-stage-end-${index}`, endError);
	}, [index, nameError, winProbabilityError, endError, rowErrorId]);

	// Reached through the DOM for the same reason as above: the design system's text field declares an
	// autofocus prop but never reads it, so passing one focuses nothing.
	useEffect(() => {
		if (!autoFocus) {
			return;
		}
		rowRef.current?.querySelector<HTMLInputElement>(`input[data-cy="opportunity-stage-name-${index}"]`)?.focus();
	}, [autoFocus, index]);

	return (
		<RowGroup>
			<Row ref={rowRef} data-cy={`opportunity-stage-row-${index}`}>
				<NameField>
					<TextField
						value={name}
						maxLength={MAX_LABEL_LENGTH}
						aria-label={formatMessage({id: 'settings.opportunity_stages.label_aria'})}
						hasError={!!nameError}
						onValueChange={onNameChange}
						onBlur={onCommit}
						data-cy={`opportunity-stage-name-${index}`}
					/>
				</NameField>
				{/* The value an opportunity takes on entering this stage. Editable on every band, the last
				    one included — only its range end is pinned, not the number a seller lands on. */}
				<EditableBoundField>
					<TextField
						value={winProbabilityText}
						aria-label={formatMessage({id: 'settings.opportunity_stages.win_probability_aria'})}
						hasError={!!winProbabilityError}
						onValueChange={onWinProbabilityChange}
						onBlur={onCommit}
						data-cy={`opportunity-stage-win-probability-${index}`}
					/>
					<PercentSuffix>%</PercentSuffix>
				</EditableBoundField>
				<RangeCell>
					<BoundField>
						<TextField
							value={fixedBoundText(start)}
							disabled
							aria-label={formatMessage({id: 'settings.opportunity_stages.start_aria'})}
							data-cy={`opportunity-stage-start-${index}`}
						/>
					</BoundField>
					<Text color={'medium'}>{formatMessage({id: 'settings.opportunity_stages.up_to'})}</Text>
					{isLast ? (
						<BoundField>
							<TextField
								value={`${endText}%`}
								disabled
								aria-label={formatMessage({id: 'settings.opportunity_stages.end_aria'})}
								data-cy={`opportunity-stage-end-${index}`}
							/>
						</BoundField>
					) : (
						<EditableBoundField>
							<TextField
								value={endText}
								aria-label={formatMessage({id: 'settings.opportunity_stages.end_aria'})}
								hasError={!!endError}
								onValueChange={onEndChange}
								onBlur={onCommit}
								data-cy={`opportunity-stage-end-${index}`}
							/>
							<PercentSuffix>%</PercentSuffix>
						</EditableBoundField>
					)}
				</RangeCell>
				{/* Shown disabled rather than removed when only one band is left: the column keeps its shape,
				    and the affordance stays where the user expects it instead of disappearing from the row
				    that happens to be last. */}
				<DeleteCell>
					<AssistiveText id={deleteLabelId}>
						{formatMessage({id: 'settings.opportunity_stages.delete_aria'})}
					</AssistiveText>
					<Button
						emphasis={EMPHASIS.LOW}
						icon={'trash'}
						iconOnly
						disabled={!canDelete}
						aria-labelledby={deleteLabelId}
						onPress={onDelete}
						data-cy={`opportunity-stage-delete-${index}`}
					/>
				</DeleteCell>
			</Row>
			<RowError data-cy={`opportunity-stage-message-slot-${index}`}>
				{rowErrors.length > 0 && (
					// role="alert" so the reason is announced when it appears, rather than only being found by
					// someone who goes looking for it after a save silently did nothing. Kept on an inner
					// element so a row with nothing wrong announces nothing and matches no error selector,
					// while the slot around it still holds its place.
					<div id={rowErrorId} role={'alert'} data-cy={`opportunity-stage-row-error-${index}`}>
						{rowErrors.map(message => (
							<Text key={message} color={'error'}>
								{message}
							</Text>
						))}
					</div>
				)}
			</RowError>
		</RowGroup>
	);
};
